import { inject } from '@angular/core';
import type { CanActivateFn, UrlTree } from '@angular/router';
import { NavigationService } from '@core/navigation/services/navigation.service';
import { AppPathsMap } from '@core/routing/app-paths';
import { AuthFacade } from '@/auth/facades/auth.facade';

/**
 * Chooses where an unmatched URL lands: a user with a session who mistyped a route
 * gets the not-found page, anyone else is sent to the front door.
 *
 * Uses the strict {@link AuthFacade.hasEstablishedSession} rather than the tolerant
 * definition `sessionGuard` needs — a visitor whose token exchange failed still
 * carries the provider marker, and treating that as logged in would strand them on a
 * 404 instead of routing them home.
 *
 * @returns {UrlTree} The not-found page for an established session, otherwise the homepage.
 */
export const redirectGuard: CanActivateFn = (): UrlTree => {
  const navigationService = inject(NavigationService);
  const authFacade = inject(AuthFacade);

  if (!authFacade.hasEstablishedSession()) {
    return navigationService.createUrlTree([AppPathsMap.Homepage]);
  }

  return navigationService.createUrlTree([AppPathsMap.Error.NotFound]);
};
